import { isOnlineCardPayment } from "./orderPaymentStatus";

const EPSILON = 0.009;

const SUCCESS_STATUSES = new Set([
  "paid",
  "success",
  "succeeded",
  "completed",
  "hold",
  "authorized",
  "blocked",
  "reserved",
  "сплачено",
  "виконано",
]);

const PENDING_STATUSES = new Set([
  "pending",
  "created",
  "processing",
  "pending_approval",
]);

const FAILED_STATUSES = new Set([
  "failed",
  "failure",
  "error",
  "declined",
  "rejected",
  "expired",
  "canceled",
  "cancelled",
]);

const REFUND_STATUS_LABELS = {
  pending: "Очікує обробки",
  processing: "Очікує обробки",
  pending_approval: "Очікує підтвердження",
  success: "Успішно",
  succeeded: "Успішно",
  completed: "Успішно",
  failed: "Помилка",
  full: "Повне повернення",
  partial: "Часткове повернення",
};

const REFUND_OPERATION_LABELS = {
  order_cancel: "Повернення коштів за скасоване замовлення",
  product_return: "Повернення коштів за повернення товарів",
};

const PAYMENT_STATUS_LABELS = {
  paid: "Оплата успішна",
  success: "Оплата успішна",
  succeeded: "Оплата успішна",
  partially_refunded: "Оплата успішна",
  refunded: "Оплата повернена",
  reversed: "Оплата повернена",
  pending: "Очікується он-лайн оплата",
  created: "Очікується он-лайн оплата",
  processing: "Оплата обробляється",
  hold: "Оплата успішна",
  authorized: "Оплата успішна",
  blocked: "Оплата успішна",
  reserved: "Оплата успішна",
  failed: "Оплата не була завершена",
  failure: "Оплата не була завершена",
  error: "Оплата не була завершена",
  declined: "Оплата не була завершена",
  rejected: "Оплата не була завершена",
  expired: "Термін оплати минув",
  canceled: "Оплату скасовано",
  cancelled: "Оплату скасовано",
};

export const normalizePaymentText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");

const getArray = (value) => (Array.isArray(value) ? value : []);

const roundMoney = (value) =>
  Math.round((Number(value) || 0) * 100) / 100;

const getPositiveAmount = (...values) => {
  for (const value of values) {
    const amount = roundMoney(value);
    if (amount > EPSILON) return amount;
  }

  return 0;
};

export const formatCustomerPaymentAmount = (value, { signed = false } = {}) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";

  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat("uk-UA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absAmount);

  if (!signed) return `${formatted} ₴`;
  if (amount < -EPSILON) return `-${formatted} ₴`;
  if (amount > EPSILON) return `+${formatted} ₴`;
  return `${formatted} ₴`;
};

export const formatCustomerPaymentDateTime = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("uk-UA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPaymentStatusValue = (order = {}) =>
  order.paymentStatus ||
  order.onlinePaymentStatus ||
  order.monoInvoiceStatus ||
  order.transactionData?.lastProviderPayload?.status ||
  order.transactionData?.rawProviderResponse?.status ||
  "";

const getPaymentStatusLabel = (status) => {
  const normalized = normalizePaymentText(status);
  return PAYMENT_STATUS_LABELS[normalized] || status || "Інформація про оплату відсутня";
};

const getRefundStatusLabel = (status) => {
  const normalized = normalizePaymentText(status);
  return REFUND_STATUS_LABELS[normalized] || status || "Очікує оновлення";
};

const getRefundStatusTone = (status) => {
  const normalized = normalizePaymentText(status);
  if (SUCCESS_STATUSES.has(normalized) || normalized === "full" || normalized === "partial") {
    return "success";
  }
  if (FAILED_STATUSES.has(normalized)) return "danger";
  if (PENDING_STATUSES.has(normalized)) return "warning";
  return "neutral";
};

const getRefundOperationLabel = (refund) => {
  const type = normalizePaymentText(refund?.type || refund?.reason);
  return REFUND_OPERATION_LABELS[type] || "Повернення коштів";
};

const getRefundOperationDate = (refund) =>
  refund.completedAt || refund.failedAt || refund.createdAt || "";

const getRefundOperationKey = (refund, index) => {
  if (refund?.returnOperationId) return refund.returnOperationId;
  if (refund?.extRef) return String(refund.extRef).replace(/-\d+$/, "");
  return refund?.refundId || `refund-${index}`;
};

const getRefundOperationStatus = (refunds = []) => {
  const statuses = refunds.map((refund) => normalizePaymentText(refund?.status));
  if (statuses.some((status) => FAILED_STATUSES.has(status))) return "failed";
  if (statuses.some((status) => PENDING_STATUSES.has(status))) return "pending";
  if (statuses.some((status) => SUCCESS_STATUSES.has(status))) return "success";
  return refunds[0]?.status || "";
};

const getRefundTimestamp = (refund) => {
  const timestamp = new Date(getRefundOperationDate(refund)).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const getRefundItems = (refunds = []) => {
  const itemsByKey = new Map();

  refunds.forEach((refund) => {
    getArray(refund?.items).forEach((item, index) => {
      const key = [
        item.productId || "",
        item.productCode || item.sku || "",
        item.name || "",
        item.weight || "",
        item.quantity || "",
        item.total || "",
        index,
      ].join("|");

      if (!itemsByKey.has(key)) {
        itemsByKey.set(key, {
          productCode: item.productCode || item.sku || "",
          name: item.name || "Товар",
          weight: item.weight || "",
          quantity: Number(item.quantity || 0),
          total: roundMoney(item.total),
        });
      }
    });
  });

  return Array.from(itemsByKey.values());
};

const groupPaymentRefundsByOperation = (refunds = []) => {
  const groups = new Map();

  refunds
    .filter((refund) => getPositiveAmount(refund?.amount) > 0)
    .forEach((refund, index) => {
      const key = getRefundOperationKey(refund, index);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(refund);
    });

  return Array.from(groups.values())
    .map((groupRefunds) => {
      const sortedRefunds = [...groupRefunds].sort(
        (left, right) => getRefundTimestamp(left) - getRefundTimestamp(right)
      );
      const firstRefund = sortedRefunds[0] || {};
      const lastRefund = sortedRefunds[sortedRefunds.length - 1] || firstRefund;

      return {
        label: getRefundOperationLabel(firstRefund),
        amount: roundMoney(
          sortedRefunds.reduce(
            (sum, refund) => sum + Number(refund?.amount || 0),
            0
          )
        ),
        status: getRefundOperationStatus(sortedRefunds),
        statusLabel: getRefundStatusLabel(getRefundOperationStatus(sortedRefunds)),
        statusTone: getRefundStatusTone(getRefundOperationStatus(sortedRefunds)),
        date: getRefundOperationDate(lastRefund),
        items: getRefundItems(sortedRefunds),
      };
    })
    .sort((left, right) => {
      const leftDate = new Date(left.date).getTime() || 0;
      const rightDate = new Date(right.date).getTime() || 0;
      return leftDate - rightDate;
    });
};

const getSuccessfulRefundedAmount = (order, refundGroups) => {
  const groupedAmount = refundGroups
    .filter((refund) => normalizePaymentText(refund.status) === "success")
    .reduce((sum, refund) => sum + Number(refund.amount || 0), 0);

  const operationAmount = getArray(order?.refundOperations)
    .filter((operation) => {
      const status = normalizePaymentText(operation?.status);
      return SUCCESS_STATUSES.has(status) || Boolean(operation?.completedAt);
    })
    .reduce((sum, operation) => sum + Number(operation?.amount || 0), 0);

  return getPositiveAmount(
    order?.totalRefundedAmount,
    groupedAmount,
    order?.refundedAmount,
    operationAmount,
    order?.refundAmount
  );
};

const getGrossPaidAmount = (order, refundedAmount) => {
  const paymentSessionsGross = getArray(order?.paymentSessions)
    .filter((session) => {
      const status = normalizePaymentText(session?.status);
      return SUCCESS_STATUSES.has(status) || Boolean(session?.paidAt);
    })
    .reduce((sum, session) => sum + Number(session?.amount || 0), 0);

  return getPositiveAmount(
    Number(order?.initialPaidAmount || 0) + Number(order?.additionalPaidAmount || 0),
    refundedAmount ? Number(order?.totalPaidAmount || 0) + refundedAmount : 0,
    paymentSessionsGross,
    order?.totalAmountSnapshot,
    order?.paidAmount,
    refundedAmount ? Number(order?.totalAmount || 0) + refundedAmount : 0,
    order?.totalAmount
  );
};

const getReturnedItemsSummary = (order = {}) => {
  const returnedItems = getArray(order.returnedItems);
  const amount = roundMoney(
    returnedItems.reduce(
      (sum, item) =>
        sum +
        Number(
          item.totalReturnAmount ||
            Number(item.returnQuantity || 0) * Number(item.price || 0)
        ),
      0
    )
  );

  return {
    count: returnedItems.length,
    amount,
    items: returnedItems.map((item) => ({
      productCode: item.productCode || "",
      name: item.name || "Товар",
      weight: item.weight || "",
      quantity: Number(item.returnQuantity || 0),
      date: item.returnDate || "",
    })),
  };
};

export const getCustomerOnlinePaymentSummary = (order = {}) => {
  if (!isOnlineCardPayment(order)) {
    return { applies: false };
  }

  const paymentStatus = normalizePaymentText(getPaymentStatusValue(order));
  const refundStatus = normalizePaymentText(order.refundStatus);
  const orderStatus = normalizePaymentText(order.status || order.orderStatus);
  const canceledOrder = orderStatus.includes("скас") || orderStatus === "canceled";
  const refundGroups = groupPaymentRefundsByOperation(order.paymentRefunds);
  const returnedItemsSummary = getReturnedItemsSummary(order);
  const refundedAmount = getSuccessfulRefundedAmount(order, refundGroups);
  const grossPaidAmount = getGrossPaidAmount(order, refundedAmount);
  const providerShowsFullRefund =
    paymentStatus === "refunded" ||
    paymentStatus === "reversed" ||
    refundStatus === "full";
  const providerShowsPartialRefund =
    paymentStatus === "partially_refunded" || refundStatus === "partial";
  const hasRefund =
    refundedAmount > EPSILON ||
    refundGroups.length > 0 ||
    providerShowsFullRefund ||
    providerShowsPartialRefund;
  const hasFullRefund =
    providerShowsFullRefund ||
    (refundedAmount > EPSILON &&
      grossPaidAmount > EPSILON &&
      refundedAmount + EPSILON >= grossPaidAmount);
  const hasPartialRefund =
    hasRefund &&
    !hasFullRefund &&
    (refundedAmount > EPSILON ||
      providerShowsPartialRefund);
  const hasPendingRefund =
    ["pending_approval", "processing", "pending"].includes(
      normalizePaymentText(order.refundStatus)
    ) ||
    refundGroups.some((refund) => refund.statusTone === "warning");
  const hasPendingAdditionalPayment =
    normalizePaymentText(order.additionalPaymentStatus) === "pending" &&
    Number(order.additionalPaymentAmount || 0) > EPSILON;
  const hasPaidAdditionalPayment =
    SUCCESS_STATUSES.has(normalizePaymentText(order.additionalPaymentStatus)) ||
    Boolean(order.additionalPaymentPaidAt);
  const isAuthorizedPayment =
    paymentStatus === "hold" ||
    paymentStatus === "authorized" ||
    paymentStatus === "blocked" ||
    paymentStatus === "reserved" ||
    normalizePaymentText(order.holdStatus) === "held" ||
    normalizePaymentText(order.holdStatus) === "authorized" ||
    normalizePaymentText(order.holdStatus) === "blocked" ||
    normalizePaymentText(order.holdStatus) === "reserved" ||
    normalizePaymentText(order.monoInvoiceStatus) === "hold";
  const isPaid =
    SUCCESS_STATUSES.has(paymentStatus) ||
    paymentStatus === "partially_refunded" ||
    isAuthorizedPayment;
  const isPendingPayment =
    PENDING_STATUSES.has(paymentStatus) && !isAuthorizedPayment;
  const isFailedPayment = FAILED_STATUSES.has(paymentStatus);

  let statusLabel = getPaymentStatusLabel(paymentStatus);
  let tone = "neutral";
  let message = "";

  if (hasPendingAdditionalPayment) {
    statusLabel = "Потрібна доплата за оновлене замовлення";
    tone = "warning";
    message = "Після оновлення замовлення потрібно виконати доплату.";
  } else if (hasPendingRefund) {
    statusLabel = "Повернення коштів очікується";
    tone = "warning";
    message = "Повернення коштів обробляється. Дані оновляться після завершення операції.";
  } else if (hasFullRefund) {
    statusLabel = "Кошти повернено на картку";
    tone = "refunded";
    message = canceledOrder
      ? "Замовлення скасовано, оплату повернено на картку."
      : "Кошти за замовлення повернено на картку.";
  } else if (hasPartialRefund) {
    statusLabel = "Оплата успішна";
    tone = "partialRefund";
    message = "Частину коштів повернено на картку.";
  } else if (isPaid) {
    statusLabel = "Оплата успішна";
    tone = "success";
    message = "Оплату за замовлення отримано.";
  } else if (canceledOrder) {
    statusLabel = isFailedPayment || !paymentStatus
      ? "Замовлення скасовано без списання коштів"
      : "Замовлення скасовано";
    tone = "danger";
    message = "Замовлення скасовано.";
  } else if (isPendingPayment) {
    statusLabel = "Очікується он-лайн оплата";
    tone = "warning";
    message = "Очікуємо підтвердження оплати.";
  } else if (isFailedPayment) {
    statusLabel = getPaymentStatusLabel(paymentStatus);
    tone = "danger";
    message = "Оплата не була завершена.";
  }

  const rows = [
    {
      label: "Сума оплати",
      value: formatCustomerPaymentAmount(
        getPositiveAmount(
          order.initialPaidAmount,
          order.totalPaidAmount && refundedAmount
            ? Number(order.totalPaidAmount) + refundedAmount
            : order.totalPaidAmount,
          order.totalAmountSnapshot,
          order.totalAmount
        )
      ),
    },
    order.paidAt && {
      label: "Дата оплати",
      value: formatCustomerPaymentDateTime(order.paidAt),
    },
    isPendingPayment &&
      order.paymentExpiresAt && {
        label: "Оплатити до",
        value: formatCustomerPaymentDateTime(order.paymentExpiresAt),
      },
    hasPendingAdditionalPayment && {
      label: "Сума доплати",
      value: formatCustomerPaymentAmount(order.additionalPaymentAmount),
      tone: "warning",
    },
    hasPaidAdditionalPayment && {
      label: "Доплату отримано",
      value: formatCustomerPaymentAmount(
        getPositiveAmount(order.additionalPaidAmount, order.additionalPaymentAmount)
      ),
      tone: "success",
    },
    refundedAmount > EPSILON && {
      label: hasFullRefund ? "Сума повернення" : "Часткове повернення",
      value: formatCustomerPaymentAmount(-refundedAmount, { signed: true }),
      tone: "danger",
    },
    canceledOrder &&
      order.cancelReason && {
        label: "Причина скасування",
        value: order.cancelReason,
      },
  ].filter(Boolean);

  const events = [
    order.paidAt && {
      tone: "success",
      text: "Оплату успішно отримано.",
      date: order.paidAt,
    },
    hasPaidAdditionalPayment && {
      tone: "success",
      text: "Доплату успішно отримано.",
      date: order.additionalPaymentPaidAt,
    },
    hasPendingAdditionalPayment && {
      tone: "warning",
      text: "Потрібна доплата за оновлене замовлення.",
      amount: order.additionalPaymentAmount,
    },
    canceledOrder && {
      tone: "danger",
      text: "Замовлення скасовано.",
      date: order.canceledAt,
    },
    returnedItemsSummary.count > 0 && {
      tone: "neutral",
      text: "Повернення товарів оформлено.",
      amount: returnedItemsSummary.amount,
    },
  ].filter(Boolean);

  return {
    applies: true,
    statusLabel,
    tone,
    message,
    rows,
    events,
    refundGroups,
    returnedItemsSummary,
    additionalPayment: hasPendingAdditionalPayment
      ? {
          amount: Number(order.additionalPaymentAmount || 0),
          url: order.additionalPaymentUrl || "",
        }
      : null,
  };
};
